/**
 * Project Management
 *
 * Single source of truth for all project/session types, helpers, serialization,
 * and the migration system.
 * Consumed by as-app, db.ts, the store, and inspector components.
 *
 * === Migrations ===
 * App developers register version-keyed migrations with setMigrations().
 * The version is the app's own, from `<as-app app-version>`. Every save stamps
 * it into metadata.appVersion. applyMigrations() is called during project load
 * and runs, in order, the migrations newer than the saved app version and not
 * newer than the current one. A file with no saved version runs all of them.
 * An app with no app-version skips versioned migrations for stamped files.
 *
 * Two wildcard keys run on every load regardless of version: '*' runs before
 * all version-keyed migrations, '>*' runs after them.
 *
 * Each version entry can migrate any combination of:
 *   uiInputs   — flat Record<string, any>
 *   customData — flat Record<string, any>
 *   session    — flat SessionParams map keyed by "packageIdent:paramIdent"
 *   pending    — same key space as session; see below
 *
 * Each field accepts either a mapping object { oldKey: newKey, removed: null }
 * or a transform function (currParams, initParams) => currParams. `initParams`
 * is the field's value as it was before any migrations ran this load, so a
 * transform can compare `currParams` (progressively updated by earlier
 * migrations in the chain) against where things started.
 *
 * `pending` migrates Project.pendingParams — session values the app had set but
 * not yet sent to the server when the project was saved. They are not in the
 * session zip, and loadProject replays them onto the live session AFTER the
 * restore, so a pending value wins over whatever a `session` migration wrote
 * for the same key.
 *
 * Renames and deletes carry over on their own: a `session` MAPPING is applied
 * to pending as well, since both use the same "pkg:param" keys. Write a
 * `pending` entry for what that can't express — usually "this migration owns
 * the key now, drop what was pending for it": { 'main:x': null }. A `pending`
 * transform additionally receives the migrated session params, read-only, as a
 * third argument. Session transforms are NOT carried over: they are written
 * against a full param map, not the sparse pending one.
 *
 * Within one entry the session mapping runs first, so a `pending` entry refers
 * to keys by their NEW names:
 *   '1.1.0': {
 *     session: { 'main:old': 'main:new' },  // pending's 'main:old' → 'main:new'
 *     pending: { 'main:new': null },        // then dropped, under the new name
 *   }
 * A `session: { key: null }` delete drops the key from pending too, so that
 * case needs no `pending` entry.
 *
 * The session structural merge (carrying old values into the current SPS
 * structure) runs after explicit migrations, so renamed/added params are
 * handled automatically. Explicit session migrations only need to cover
 * renames or value transforms.
 *
 * Usage:
 *   import { setMigrations } from 'as-components/data/projectManagement.ts';
 *
 *   setMigrations({
 *     '1.2.0': {
 *       uiInputs: { 'wall-thickness': 'wallThickness', 'legacyParam': null },
 *       session:  { 'main:oldParam': 'main:newParam' },
 *     },
 *     '2.0.0': {
 *       uiInputs:   (inputs) => ({ ...inputs, height: inputs.height / 10 }),
 *       customData: { 'legacyField': 'newField' },
 *     },
 *     '>*': {   // every load, after all version-keyed migrations
 *       session: (currParams, initParams) => {
 *         currParams['main:preview'] = initParams['main:preview'] ?? false;
 *         return currParams;
 *       },
 *       // The line above owns main:preview, so drop any pending value for it —
 *       // the replay after the restore would otherwise overwrite it.
 *       pending: { 'main:preview': null },
 *     },
 *   });
 */

import type { VPData } from './data-interfaces.ts';
import type { DeriveSpec, DerivedStore } from '../as-components';
import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import { internalData, deriveFromStore } from '../store/store.ts';
import { createSource, type SubscribeOpts } from '../store/subscriptions.ts';
import { addAsGlobals } from '../asGlobals.ts';
import { params } from '../store/paramStore.ts';
import { sendEv } from './events.ts';
import { INTERNAL_MIGRATIONS } from './internalMigrations.ts';
import { devlog } from '../util/utils.ts';
import packageJson from '../../package.json';

// ============================================================================
// Types
// ============================================================================

export type ProjectID = string;
export type AppID = string;

export interface ProjectMetadata {
  id: ProjectID;
  name: string;
  appId: AppID;
  /** <as-app app-version> at save time. Keys the app migration chain;
   *  undefined means the file predates the stamp or the app sets no version. */
  appVersion?: string;
  /** as-components version at save time. Keys the internal migration chain;
   *  undefined means the file predates the stamp. */
  componentsVersion?: string;
  createdAt: Date;
  updatedAt: Date;
  savedAt?: Date;        // undefined = never saved
  authorId?: string;
}

/** An output mesh snapshot stored within a project */
export interface OutputMesh {
  meshId: string;
  meshName: string;
  stlData?: Blob;
}

export interface Project {
  metadata: ProjectMetadata;
  /**
   * All input state keyed by element ID.
   * Includes scalar values (numbers, strings, booleans) and file objects.
   */
  uiInputs: Record<string, any>;
  
  /** Paramate session backup zip (from saveSession) */
  paramateSession?: Blob;

  /**
   * Params that were pending (set with setParamValue(..., false), no configure
   * yet) when the session was captured. The session blob never contains these —
   * loadProject replays them onto the restored session so the project comes
   * back in the same pending state it was saved in. Flat "pkg:param" → value
   * map (see AsConfigurator.getPendingParams).
   */
  pendingParams?: SessionParams;

  /** WIP /NOT USED Output mesh snapshots produced during the session */
  outputMeshes?: OutputMesh[];
  /**
   * App-defined data that is persisted with the project and restored
   * when the project is loaded (see `as-after-project-loaded`).
   * Also reflected in `currentProjectData.customData`.
   */
  customData?: Record<string, any>;

  /**
   * WIP / MAY DELETE File System Access API handle for the linked .asproject file.
   * Present when the user has chosen a local file location.
   * FileSystemFileHandle is structured-cloneable, so it survives IDB round-trips.
   * undefined = never linked to a local file ("undownloaded").
   */
  fileHandle?: FileSystemFileHandle;
  // outputMeshVPData?: VPData[];  // disabled - raw geometry snapshots, reserved for future use
}

// ============================================================================
// Runtime project data (mutable singleton — read/write directly from app code)
// ============================================================================

const _rawAppData: Record<string, any> = {};
let _suppressAppDataEvents = false;

/**
 * Subscribers and the commit protocol — see store/subscriptions.ts, the same one
 * behind internalData, paramStore and sysData. appData builds on it directly
 * rather than through `reactiveStore()` because its keys are whatever the app
 * puts there, not a fixed schema.
 *
 * NOTE: top-level keys only — nested objects are treated as immutable values,
 * so replace the whole object (`appData.foo = {...}`) to notify.
 */
const _appDataSource = createSource<Record<string, any>>({
  name: 'APP-DATA',
  snapshot: () => ({ ..._rawAppData }),
  resolve: (target) => Array.isArray(target)
    ? target.map((key: string) => _rawAppData[key])
    : target === undefined ? { ..._rawAppData } : _rawAppData[target as string],
  keysOf: (target) => (Array.isArray(target) ? target : [target]) as string[],
});

/**
 * Announce a set of changed keys: wake the per-key subscribers, then the
 * whole-object ones, then the window events. The single commit point for every
 * appData write — both proxy traps and setAppData/patchAppData all end here.
 *
 * Fires both the new `as-app-data-*` events and the deprecated
 * `as-custom-data-*` ones during the customData -> appData transition, so
 * listeners on either name keep working. Drop the as-custom-data-* dispatches
 * once external consumers (e.g. Outlines2) have migrated off them.
 */
function _commitAppData(changedKeys: Iterable<string>, silent = false): void {
  if (_suppressAppDataEvents) return;

  _appDataSource.commit(changedKeys);

  const snapshot = { ..._rawAppData };
  // import { devlog } from '../util/utils.ts'; // can't import here due to circular deps usually, use console.debug
  console.debug(`[APP-DATA] Notification: silent=${silent}, keys=[${Object.keys(snapshot)}]`);

  sendEv(window, 'as-app-data-updated', {
    detail: { appData: snapshot, customData: snapshot, silent }
  });
  sendEv(window, 'as-custom-data-updated', {
    detail: { appData: snapshot, customData: snapshot, silent }
  });

  if (!silent) {
    sendEv(window, 'as-app-data-changed', {
      detail: { appData: snapshot, customData: snapshot }
    });
    sendEv(window, 'as-custom-data-changed', {
      detail: { appData: snapshot, customData: snapshot }
    });
    sendEv(window, 'as-project-change');
  }
}

/** Overloads can't live in an object literal — see the `appData` Proxy below. */
function _subscribeAppData(key: string, cb: (value: any, key: string) => void): () => void;
function _subscribeAppData(keys: readonly string[], cb: (values: any[], keys: readonly string[]) => void): () => void;
function _subscribeAppData<T extends Record<string, string>>(aliases: T, cb: (values: { [A in keyof T]: any }) => void): () => void;
function _subscribeAppData(target: any, cb: any): () => void {
  // coalesce: false — appData bindings have always recomputed once per changed
  // key, and firing once for a multi-key change would be a silent behaviour
  // change in existing apps. params and sysData coalesce; this one doesn't.
  if (Array.isArray(target) || typeof target === 'string') {
    return _appDataSource.subscribe(target, cb, { coalesce: false });
  }
  const entries = Object.entries(target as Record<string, string>);
  return _appDataSource.subscribe(entries.map(([, key]) => key), () => {
    const out: Record<string, any> = {};
    for (const [alias, key] of entries) out[alias] = _rawAppData[key];
    cb(out);
  }, { coalesce: false });
}

/** The value a `subscribe` target resolves to right now — used by the element
 *  binders in components/base.ts to apply before the first change. */
export function resolveAppData(target: any): any {
  return target === undefined ? { ..._rawAppData } : _appDataSource.resolve(target);
}

/**
 * The app's own state — whatever it puts there, persisted with the project.
 *
 *   appData.selectedPreset = 'compact'             // plain write, marks dirty
 *   delete appData.oldKey
 *   appData.patch({ a: 1, b: 2 }, silent)          // merge several keys
 *   appData.set({ a: 1 }, silent)                  // replace the whole object
 *   appData.subscribe('preset', (v) => ...)        // react to one
 *   appData.subscribe(['a', 'b'], ([a, b]) => ...) // react to several
 *   appData.subscribe({ p: 'preset' }, ({ p }) => ...)
 *   appData.subscribeAll((data) => ...)            // react to any change
 *
 * The same object as `currentProjectData.appData`. `<as-app>.appData` is this
 * plus the project-lifecycle helpers that need the element — `defaults`,
 * `revert`, `patchDefaults`, `derive`.
 *
 * `patch`/`set`/`subscribe`/`subscribeAll`/`snapshot`/`print` are answered by
 * the Proxy rather than read out of the data. They don't appear in
 * `Object.keys(appData)` or a spread, so enumerating the data stays clean — but
 * a project holding a key by one of those names can't be read through this
 * object.
 *
 * The read-only counterpart is `sysData` (store/sysDataStore.ts); `params`
 * (store/paramStore.ts) carries the same pair for the CAD table.
 *
 * NOTE: top-level keys only. Nested objects are treated as immutable values, so
 * replace the whole object (`appData.foo = {...}`) to notify.
 */
export const appData: Record<string, any> = new Proxy(_rawAppData, {
  get(target, prop, receiver) {
    if (typeof prop === 'string') {
      switch (prop) {
        case 'patch':        return (patch: Record<string, any>, silent = false) => currentProjectData.patchAppData(patch, silent);
        case 'set':          return (next: Record<string, any>, silent = false) => currentProjectData.setAppData(next, silent);
        case 'subscribe':    return _subscribeAppData;
        case 'subscribeAll': return (cb: (data: Record<string, any>) => void) => _appDataSource.subscribe(cb);
        case 'snapshot':     return () => ({ ..._rawAppData });
        case 'print':        return () => console.log({ ..._rawAppData });
      }
    }
    return Reflect.get(target, prop, receiver);
  },
  set(target, prop, value) {
    if (target[prop as string] === value) return true;
    target[prop as string] = value;
    _commitAppData([prop as string]);
    return true;
  },
  deleteProperty(target, prop) {
    if (!(prop in target)) return true;
    delete target[prop as string];
    _commitAppData([prop as string]);
    return true;
  }
});

/** The name the rest of this file already uses for it. */
const _appDataProxy = appData;

export const currentProjectData: {
  info: Record<string, any>;
  /** Paramate configSession ID — written by as-configurator on init. An alias
   *  onto `internalData.sessionId`, which is also what `app.sysData.sessionId`
   *  reports; there is one value, not two. */
  sessionId: string;
  uiState: Record<string, any>;
  /** The Paramate param table as of the last publish — read-only, and the same
   *  object store/paramStore.ts holds. Empty until the configurator initialises.
   *  Use params.subscribe()/bindParams() to react to it. */
  readonly paramateParamCache: Record<string, any>;
  meshes: Record<string, VPData>;
  appDefined: Record<string, any>;
  
  
  uiInputs: Record<string, any>;
  /** Mirrors Project.customData for the currently active project */
  appData: Record<string, any>;
  //TODO:@REMOVE
  /** @deprecated use `appData` instead. Same underlying data, kept working
   *  for existing consumers (e.g. Outlines2) during the customData -> appData
   *  rename. */
  customData: Record<string, any>;

  /** Update appData. Set silent=true to skip history entry and dirty-marking. */
  setAppData(v: Record<string, any>, silent?: boolean): void;
  //TODO:@REMOVE
  /** @deprecated use `setAppData` instead. */
  setCustomData(v: Record<string, any>, silent?: boolean): void;
  /**
   * Merge a patch into appData in place — unlike setAppData (a wholesale
   * replace), keys absent from `patch` are left untouched. Set silent=true to
   * skip history entry and dirty-marking.
   */
  patchAppData(patch: Record<string, any>, silent?: boolean): void;
  //TODO:@REMOVE
  /** @deprecated use `patchAppData` instead. */
  patchCustomData(patch: Record<string, any>, silent?: boolean): void;
  /**
   * Builds a computed store from appData (see `deriveAppData` in this
   * file for details). Exposed here alongside subscribe/subscribeKey so it's
   * easy to find, rather than a separate top-level import.
   */
  derive<T extends Record<string, DeriveAppDataSpec<any>>>(spec: T): DerivedStore<T>;
} = {
  info: {},
  // Forwards to internalData, same as uiInputs below: the session id is
  // library state, not app data, and sysData reports it from there. Kept on
  // this object because that is where callers have always found it.
  get sessionId() { return internalData.sessionId ?? ''; },
  set sessionId(v: string) { internalData.sessionId = v; },
  uiState: {},
  get uiInputs() { return internalData.uiInputs; },
  set uiInputs(v: Record<string, any>) { internalData.uiInputs = v; },
  get paramateParamCache() { return params.all(); },
  meshes: {},
  appDefined: {},
  get appData() { return _appDataProxy; },
  set appData(v: Record<string, any>) {
    this.setAppData(v, false);
  },
  //TODO:@REMOVE
  get customData() { return _appDataProxy; },
  set customData(v: Record<string, any>) {
    this.setAppData(v, false);
  },
  setAppData(v: Record<string, any>, silent = false) {
    // Capture which top-level keys actually change so per-key subscribers fire
    // exactly once each on a bulk replace (e.g. a project restore).
    const before = { ..._rawAppData };
    _suppressAppDataEvents = true;
    for (const k in _rawAppData) delete (_rawAppData as any)[k];
    Object.assign(_rawAppData, v);
    _suppressAppDataEvents = false;
    const allKeys = new Set<string>([...Object.keys(before), ...Object.keys(_rawAppData)]);
    _commitAppData([...allKeys].filter(k => before[k] !== _rawAppData[k]), silent);
  },
  //TODO:@REMOVE
  setCustomData(v: Record<string, any>, silent = false) {
    this.setAppData(v, silent);
  },
  patchAppData(patch: Record<string, any>, silent = false) {
    const changedKeys = Object.keys(patch).filter(k => _rawAppData[k] !== patch[k]);
    Object.assign(_rawAppData, patch);
    _commitAppData(changedKeys, silent);
  },
  //TODO:@REMOVE
  patchCustomData(patch: Record<string, any>, silent = false) {
    this.patchAppData(patch, silent);
  },
  // Assigned below, once `deriveAppData` is declared (function declarations
  // are hoisted, so the reference is valid here regardless of file order).
  derive: deriveAppData,
};

addAsGlobals({ currentProjectData });

// ============================================================================
// deriveAppData — computed values built from appData. See docs/dataBinding.md
// ============================================================================

/** Same shape as the generic `DeriveSpec` (store/store.ts) — kept as its own
 *  name since it's the established public name for this specific binding
 *  (currentProjectData.derive), documented in docs/dataBinding.md. */
export type DeriveAppDataSpec<T> = DeriveSpec<T>;

//TODO:@REMOVE
/** @deprecated use `DeriveAppDataSpec` instead. Identical shape. */
export type DeriveCustomDataSpec<T> = DeriveAppDataSpec<T>;

/**
 * Builds one entry for `currentProjectData.derive({...})`.
 *
 * - `deps`: which appData keys to watch.
 * - `compute`: gets their current values, in the same order, returns the result.
 * - Only appData is ever a dependency — nothing else can feed in.
 *
 * ```ts
 * currentProjectData.derive({
 *   busy: deriveFrom(['samWorking', 'tracingInProgress'], ([samWorking, tracingInProgress]) => samWorking || tracingInProgress),
 * });
 * ```
 */
export function deriveFrom<R>(deps: readonly string[], compute: (inputs: any[]) => R): DeriveAppDataSpec<R> {
  return { deps, get: compute };
}

/**
 * A computed store built from appData. Shared implementation with
 * reactiveStore()'s `.derive()` — see `deriveFromStore` in store/store.ts.
 *
 * Not exported on its own — call it as `currentProjectData.derive(spec)`.
 */
function deriveAppData<T extends Record<string, DeriveAppDataSpec<any>>>(spec: T): DerivedStore<T> {
  return deriveFromStore(_appDataSource, key => _rawAppData[key], spec);
}

// ============================================================================
// Helpers
// ============================================================================

function generateProjectId(): ProjectID {
  return `proj-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create a new project with default values.
 * @param appId      - The application ID this project belongs to
 * @param name       - Optional project name (defaults to "Untitled")
 * @param appVersion - Optional app version to embed in metadata
 */
export function createNewProject(appId: AppID, name?: string, appVersion?: string): Project {
  const now = new Date();
  return {
    metadata: {
      id: generateProjectId(),
      name: name?.trim() || 'Untitled',
      appId,
      ...(appVersion !== undefined && { appVersion }),
      createdAt: now,
      updatedAt: now,
      savedAt: undefined,
    },
    uiInputs: {},
    paramateSession: undefined,
    customData: {},
  };
}

/**
 * Return true if the project has unsaved changes.
 * (Never saved, or updated since last save.)
 */
export function isProjectUnsaved(project: Project): boolean {
  if (!project.metadata.savedAt) return true;
  return project.metadata.updatedAt > project.metadata.savedAt;
}

/**
 * Return true if the project has been exported to an .asproject file at least once.
 * False = "undownloaded" — only exists in IDB, never exported.
 *
 * Intentionally based on savedAt, not fileHandle — fileHandle is a Chrome/Edge
 * enhancement only; savedAt is set on every export path (file handle or download).
 */
export function isProjectDownloaded(project: Project): boolean {
  return !!project.metadata.savedAt;
}

/** Return true if the project still has the default "Untitled" name. */
export function isProjectUntitled(project: Project): boolean {
  return project.metadata.name === 'Untitled';
}

/** Return a copy of the project with savedAt set to now. */
export function markProjectSaved(project: Project): Project {
  return {
    ...project,
    metadata: { ...project.metadata, savedAt: new Date() },
  };
}

/** Return a copy of the project with updatedAt bumped to now. */
export function touchProject(project: Project): Project {
  return {
    ...project,
    metadata: { ...project.metadata, updatedAt: new Date() },
  };
}

// ============================================================================
// .asproject serialization (zip via fflate)
// ============================================================================

async function blobToU8(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Serialize a project to a zip Blob suitable for writing as an .asproject file.
 * Zip entries:
 *   project.json          — metadata, uiInputs, customData, outputMeshes index
 *   session.bin           — paramateSession (if present)
 *   meshes/<meshId>.stl   — each output mesh STL (if present)
 *
 * fileHandle is intentionally omitted from the zip.
 */
export async function serializeProject(project: Project): Promise<Blob> {
  const manifest: any = {
    version: 1,
    metadata: {
      ...project.metadata,
      // Stamped at write time, not at create time, so it names the version
      // that actually produced this file.
      componentsVersion: packageJson.version,
      // Same for the app's own version, when the app sets one.
      ...(internalData.appVersion !== undefined && { appVersion: internalData.appVersion }),
      createdAt: project.metadata.createdAt instanceof Date
        ? project.metadata.createdAt.toISOString()
        : project.metadata.createdAt,
      updatedAt: project.metadata.updatedAt instanceof Date
        ? project.metadata.updatedAt.toISOString()
        : project.metadata.updatedAt,
      savedAt: project.metadata.savedAt instanceof Date
        ? project.metadata.savedAt.toISOString()
        : (project.metadata.savedAt ?? null),
    },
    uiInputs: project.uiInputs,
    customData: project.customData ?? {},
    outputMeshes: (project.outputMeshes ?? []).map(m => ({
      meshId: m.meshId,
      meshName: m.meshName,
      hasStl: !!m.stlData,
    })),
    hasSession: !!project.paramateSession,
    pendingParams: project.pendingParams ?? null,
  };

  const files: Record<string, Uint8Array> = {
    'project.json': strToU8(JSON.stringify(manifest)),
  };

  if (project.paramateSession) {
    files['session.bin'] = await blobToU8(project.paramateSession);
  }

  for (const mesh of project.outputMeshes ?? []) {
    if (mesh.stlData) {
      files[`meshes/${mesh.meshId}.stl`] = await blobToU8(mesh.stlData);
    }
  }

  return new Blob([zipSync(files, { level: 6 })], { type: 'application/zip' });
}

/**
 * Deserialize an .asproject zip Blob back into a Project.
 * fileHandle will be undefined — caller must re-attach it.
 */
export async function deserializeProject(blob: Blob): Promise<Project> {
  const files = unzipSync(new Uint8Array(await blob.arrayBuffer()));

  const manifest = JSON.parse(strFromU8(files['project.json']));

  const metadata: ProjectMetadata = {
    ...manifest.metadata,
    name: manifest.metadata.name?.trim() || 'Untitled',
    createdAt: new Date(manifest.metadata.createdAt),
    updatedAt: new Date(manifest.metadata.updatedAt),
    savedAt: manifest.metadata.savedAt ? new Date(manifest.metadata.savedAt) : undefined,
  };

  const outputMeshes: OutputMesh[] = (manifest.outputMeshes ?? []).map((m: any) => ({
    meshId: m.meshId,
    meshName: m.meshName,
    stlData: files[`meshes/${m.meshId}.stl`]
      ? new Blob([files[`meshes/${m.meshId}.stl`]], { type: 'application/octet-stream' })
      : undefined,
  }));

  return {
    metadata,
    uiInputs: manifest.uiInputs ?? {},
    customData: manifest.customData ?? {},
    paramateSession: files['session.bin']
      ? new Blob([files['session.bin']], { type: 'application/zip' })
      : undefined,
    pendingParams: manifest.pendingParams ?? undefined,
    outputMeshes,
    fileHandle: undefined,
  };
}

/**
 * Wrap a bare Paramate session zip (a `parameters.json`-only export with no
 * `project.json` wrapper, e.g. an older custom save path or a hand-exported
 * session) into a fresh Project. The raw zip is kept as the `paramateSession`
 * so the normal load + migration pipeline can structural-merge it into the
 * current session structure. appVersion is intentionally left undefined so
 * every registered migration runs and applyMigrations performs the merge.
 *
 * This is deliberately NOT done automatically by deserializeProject — call it
 * explicitly when you know you're handling a bare session rather than an
 * `.asproject`.
 */
export function projectFromBareSession(blob: Blob): Project {
  const now = new Date();
  return {
    metadata: {
      id: generateProjectId(),
      name: 'Untitled',
      appId: internalData.appId ?? '',
      createdAt: now,
      updatedAt: now,
      savedAt: undefined,
    },
    uiInputs: {},
    customData: {},
    paramateSession: new Blob([blob], { type: 'application/zip' }),
    outputMeshes: [],
    fileHandle: undefined,
  };
}

// ============================================================================
// Migration types
// ============================================================================

/** Flat map of Paramate session params keyed by "packageIdent:paramIdent" */
export type SessionParams = Record<string, any>;

/** Rename/delete mapping: { oldKey: newKey } or { oldKey: null } to delete */
export type FieldMapping = Record<string, string | null>;

export type FieldTransform<T> = (currParams: T, initParams: T) => T;

export type MigrationField<T> = FieldMapping | FieldTransform<T>;

/**
 * A `pending` transform gets one extra argument the other fields don't: the
 * session params as they came out of the `session` migrations, read-only. Use
 * it to decide what to keep by comparing against the values being restored.
 */
export type PendingTransform = (
  currPending: SessionParams,
  initPending: SessionParams,
  sessionParams: SessionParams,
  droppedKeys: readonly string[],
) => SessionParams;

export type PendingMigrationField = FieldMapping | PendingTransform;

export interface MigrationEntry {
  uiInputs?:   MigrationField<Record<string, any>>;
  customData?: MigrationField<Record<string, any>>;
  session?:    MigrationField<SessionParams>;
  /**
   * Pending params (Project.pendingParams) — session values the app had set but
   * not sent to the server when the project was saved. They are replayed onto
   * the session AFTER the restore, so a pending value wins over whatever a
   * `session` migration wrote for the same key.
   *
   * Renames and deletes are already handled: a `session` MAPPING applies to
   * pending automatically, since both sides use the same "pkg:param" keys.
   * This field is for what that can't express, most often "this migration now
   * owns the key, so drop whatever was pending for it": `{ key: null }`. A
   * transform gets the migrated session params as a third argument, and the
   * session/pending keys the internal chain deleted as a fourth (see
   * docs/internalMigrations.md). Runs after the `session` migrations.
   */
  pending?:    PendingMigrationField;
}

/**
 * Version string -> migration entry.
 *
 * Two wildcard keys run on EVERY load, regardless of the saved/current version:
 *   '*'  — runs before all version-keyed migrations
 *   '>*' — runs after all version-keyed migrations
 * Use them for unconditional restore fix-ups (resetting transient params,
 * dropping keys so the current session's values win in the structural merge).
 */
export type MigrationMap = Record<string, MigrationEntry>;

/** Wildcard key: entry runs on every load, before all version-keyed migrations. */
export const WILDCARD_BEFORE = '*';
/** Wildcard key: entry runs on every load, after all version-keyed migrations. */
export const WILDCARD_AFTER = '>*';

/**
 * Session params that are never restored from a saved project — the current
 * (live) session's values always win the structural merge. TrCAD script
 * metadata reflects the running script build, not user state, so restoring a
 * stale saved value would misreport the version after load. Deleting a key
 * absent from a given app's session is a no-op.
 */
export const NEVER_RESTORE_SESSION_PARAMS = [
  'main:metadata:scriptVersion',
  'main:metadata:trcadVersion',
];

// ============================================================================
// Migration registry
// ============================================================================

let _migrations: MigrationMap = {};
let _warnedNoAppVersion = false;

/**
 * Register the app's migrations. Version keys are compared against the app's
 * `app-version`, not the as-components version.
 */
export function setMigrations(migrations: MigrationMap): void {
  _migrations = migrations;
}

const isWildcard = (key: string) => key === WILDCARD_BEFORE || key === WILDCARD_AFTER;

// ============================================================================
// Migration helpers (internal)
// ============================================================================

function compareVersions(a: string, b: string): number {
  const av = a.split('.').map(n => parseInt(n, 10));
  const bv = b.split('.').map(n => parseInt(n, 10));
  for (let i = 0; i < Math.max(av.length, bv.length); i++) {
    const diff = (av[i] ?? 0) - (bv[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function getApplicableMigrations(
  migrations: MigrationMap,
  savedVersion: string | undefined,
  currentVersion: string | undefined
): [string, MigrationEntry][] {
  const entries = Object.entries(migrations);
  const versioned = entries
    .filter(([version]) => !isWildcard(version))
    .filter(([version]) => {
      if (!savedVersion) return true; // no saved version, run all
      // Saved but no current version: no way to place the file, so skip.
      if (!currentVersion) return false;
      return compareVersions(version, savedVersion) > 0 &&
             compareVersions(version, currentVersion) <= 0;
    })
    .sort(([a], [b]) => compareVersions(a, b));
  return [
    ...entries.filter(([v]) => v === WILDCARD_BEFORE),
    ...versioned,
    ...entries.filter(([v]) => v === WILDCARD_AFTER),
  ];
}

function applyFieldMigration<T extends Record<string, any>>(
  currParams: T,
  migration: MigrationField<T> | PendingMigrationField,
  initParams: T,
  // Extra read-only context for the field types that take one (pending
  // transforms get the migrated session params); undefined for the rest.
  context?: SessionParams,
  // Session/pending keys the internal chain deleted. Pending transforms only.
  droppedKeys: readonly string[] = []
): T {
  if (typeof migration === 'function') {
    const transform = migration as (curr: T, init: T, context: SessionParams, dropped: readonly string[]) => T;
    return transform({ ...currParams }, { ...initParams }, context as SessionParams, droppedKeys);
  }
  const result: Record<string, any> = { ...currParams };
  for (const [oldKey, newKey] of Object.entries(migration)) {
    if (!(oldKey in result)) continue;
    if (newKey === null) {
      delete result[oldKey];
    } else {
      result[newKey] = result[oldKey];
      if (newKey !== oldKey) delete result[oldKey];
    }
  }
  return result as T;
}

/** Build a flat { "pkg:param": value } map from a parameters.json object */
function buildFlatParams(paramsJson: any): SessionParams {
  const flat: SessionParams = {};
  const packages = paramsJson?.objects?.[0]?.packages ?? [];
  for (const pkg of packages) {
    for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
      // Sanitize "undefined" strings that can appear in corrupt/old sessions.
      // The legacy SPS restore in the apps checks for this too. Without it,
      // numeric params would reach TrCAD as NaN.
      const raw = param.value;
      flat[`${pkg.ident}:${param.ident}`] = String(raw).includes('undefined')
        ? (param.attributes?.value ?? raw)
        : raw;
    }
  }
  return flat;
}

/**
 * Build a flat { "pkg:param": changed } map from a parameters.json object.
 *
 * The saved `attributes.changed` flag is carried through restore verbatim rather
 * than overwritten. Forcing every restored param to changed=true makes TrCAD treat
 * untouched/derived params as fresh user edits, which fires conditional recompute
 * branches in scripts with coupled params (e.g. Stabilizers
 * `if (taperAngle.changed) bottomDiam = bottomDiamFromParams(...)`) and corrupts the
 * restored value. A saved session is an internally-consistent snapshot — restoring
 * it as "nothing changed" lets TrCAD re-derive coupled params from consistent values.
 */
function buildChangedMap(paramsJson: any): Record<string, boolean> {
  const changed: Record<string, boolean> = {};
  const packages = paramsJson?.objects?.[0]?.packages ?? [];
  for (const pkg of packages) {
    for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
      changed[`${pkg.ident}:${param.ident}`] = String(param.attributes?.changed) === 'true';
    }
  }
  return changed;
}

/** Write a flat param map back into a parameters.json structure (in-place clone) */
function applyFlatParamsToStructure(
  paramsJson: any,
  flat: SessionParams,
  changedMap: Record<string, boolean>
): any {
  const result = JSON.parse(JSON.stringify(paramsJson));
  const packages = result?.objects?.[0]?.packages ?? [];
  for (const pkg of packages) {
    for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
      const key = `${pkg.ident}:${param.ident}`;
      if (key in flat) {
        param.value = flat[key];
        // Preserve the saved changed flag (default false for params the migration
        // renamed/introduced). Never force true — see buildChangedMap.
        if (param.attributes) param.attributes.changed = changedMap[key] ?? false;
      }
    }
  }
  return result;
}

/**
 * Append params present in the old session but missing from the merged (new)
 * structure, matched into their package by ident.
 *
 * The structural merge target is the *current live* Paramate session, which only
 * contains params that are currently exposed. TrCAD scripts conditionally expose
 * params behind toggles (e.g. bottomHoleDiam/counterboreDiam behind addBottomHole/
 * addCounterbore), so when the configurator has been reset to defaults (toggles off)
 * those params are absent from the target — and applyFlatParamsToStructure silently
 * drops their saved values, re-defaulting them on restore. Carrying the old param
 * over (with its saved value + its saved changed flag) lets TrCAD honor the saved
 * value once the enabling toggle re-activates. Genuinely-removed params carried over
 * are harmless — TrCAD ignores unknown params on restore.
 */
function carryOverMissingParams(
  targetJson: any,
  oldJson: any,
  flat: SessionParams,
  changedMap: Record<string, boolean>
): void {
  const targetPkgs: any[] = targetJson?.objects?.[0]?.packages ?? [];
  const oldPkgs: any[] = oldJson?.objects?.[0]?.packages ?? [];
  const byIdent = new Map<string, any>(targetPkgs.map(p => [p.ident, p]));

  for (const oldPkg of oldPkgs) {
    const tgtPkg = byIdent.get(oldPkg.ident);
    if (!tgtPkg) continue; // package absent from current structure — out of scope
    const have = new Set<string>(
      [...(tgtPkg.oparams ?? []), ...(tgtPkg.pparams ?? [])].map((p: any) => p.ident)
    );
    for (const isPublic of [false, true]) {
      const oldList: any[] = (isPublic ? oldPkg.pparams : oldPkg.oparams) ?? [];
      const tgtList: any[] = isPublic ? (tgtPkg.pparams ??= []) : (tgtPkg.oparams ??= []);
      for (const oldParam of oldList) {
        if (have.has(oldParam.ident)) continue;
        const key = `${oldPkg.ident}:${oldParam.ident}`;
        // Respect migrations: a key renamed or deleted (null) in `flat` must NOT be
        // re-introduced under its old ident. Only carry over keys still in `flat`.
        if (!(key in flat)) continue;
        const clone = JSON.parse(JSON.stringify(oldParam));
        clone.value = flat[key];
        if (clone.attributes) clone.attributes.changed = changedMap[key] ?? false;
        tgtList.push(clone);
      }
    }
  }
}

/**
 * Core session processor:
 * 1. Unpack old session → flat param map
 * 2. Apply session migrations (mappings / transforms)
 * 3. If newStructureBlob provided: merge migrated values into new structure
 *    (structural merge — new params get defaults, removed params are dropped)
 *    Otherwise: write values back into old structure
 * 4. Repack and return the new session blob, plus the migrated flat map —
 *    pending migrations run against it (see PendingTransform)
 */
async function processSession(
  oldSessionBlob: Blob,
  migrations: MigrationField<SessionParams>[],
  newStructureBlob?: Blob,
  // How many leading entries of `migrations` came from the internal chain.
  internalSessionCount = 0
): Promise<{ blob: Blob; flat: SessionParams; internalDropped: string[] }> {
  const oldZip = unzipSync(new Uint8Array(await oldSessionBlob.arrayBuffer()));
  const oldParamsJson = JSON.parse(strFromU8(oldZip['parameters.json']));

  let flat = buildFlatParams(oldParamsJson);
  // Saved changed flags, keyed by pre-migration "pkg:ident". Carried through restore
  // verbatim; renamed/introduced params default to false (see buildChangedMap).
  const changedMap = buildChangedMap(oldParamsJson);
  // Snapshot before any migration in the chain runs, so later transforms can
  // compare their progressively-migrated `flat` against the as-saved values.
  const initFlat = { ...flat };

  const internalDropped: string[] = [];
  migrations.forEach((migration, i) => {
    const before = Object.keys(flat);
    flat = applyFieldMigration(flat, migration, initFlat);
    // Only the internal entries report drops; they come first in the list.
    if (i < internalSessionCount) {
      for (const key of before) if (!(key in flat)) internalDropped.push(key);
    }
  });

  // Script metadata always reflects the current build: absent from the flat map,
  // these keep the live structure's values in applyFlatParamsToStructure and are
  // not re-introduced by carryOverMissingParams.
  for (const key of NEVER_RESTORE_SESSION_PARAMS) delete flat[key];

  let targetParamsJson: any;
  let filesJson: Uint8Array;

  const migratedFlat = { ...flat };

  if (newStructureBlob) {
    const newZip = unzipSync(new Uint8Array(await newStructureBlob.arrayBuffer()));
    targetParamsJson = applyFlatParamsToStructure(
      JSON.parse(strFromU8(newZip['parameters.json'])),
      flat,
      changedMap
    );
    // Preserve saved params that are absent from the current (possibly toggle-reduced)
    // structure so conditionally-exposed TrCAD params don't re-default on restore.
    carryOverMissingParams(targetParamsJson, oldParamsJson, flat, changedMap);
    // Old files.json carries the uploaded-file index; old binary assets follow below
    filesJson = oldZip['files.json'] ?? newZip['files.json'];
  } else {
    targetParamsJson = applyFlatParamsToStructure(oldParamsJson, flat, changedMap);
    filesJson = oldZip['files.json'];
  }

  const outFiles: Record<string, Uint8Array> = {
    'parameters.json': strToU8(JSON.stringify(targetParamsJson)),
  };
  if (filesJson) outFiles['files.json'] = filesJson;

  // Carry over binary file assets from the old session
  for (const [name, data] of Object.entries(oldZip)) {
    if (name.startsWith('files/')) outFiles[name] = data as Uint8Array;
  }

  return {
    blob: new Blob([zipSync(outFiles, { level: 6 })], { type: 'application/zip' }),
    flat: migratedFlat,
    internalDropped,
  };
}

// ============================================================================
// applyMigrations — public API
// ============================================================================

/**
 * Apply the internal migrations, then the app's migrations between the saved
 * metadata.appVersion and currentAppVersion (the app's `app-version`).
 *
 * Pass currentSessionBlob (from configuratorEl.getSessionBlob()) to enable the
 * structural merge — old param values are carried into the new Paramate
 * structure after explicit session migrations run.
 *
 * Pending params are migrated too (see MigrationEntry.pending) and come back on
 * the returned project — that is what loadProject replays after the restore.
 *
 * Returns a new project object; the original is not mutated.
 */
export async function applyMigrations(
  project: Project,
  currentAppVersion: string | undefined,
  currentSessionBlob?: Blob
): Promise<Project> {
  if (currentAppVersion === undefined && !_warnedNoAppVersion &&
      Object.keys(_migrations).some(key => !isWildcard(key))) {
    _warnedNoAppVersion = true;
    devlog('[MIGRATIONS] Versioned app migrations are registered but <as-app> has no app-version. They only run for files saved without a version.');
  }

  // Two chains, the library's then the app's, each keyed on its own version.
  // An app migration then always sees a current library shape.
  const internal = getApplicableMigrations(
    INTERNAL_MIGRATIONS, project.metadata.componentsVersion, packageJson.version);
  const applicable = [
    ...internal,
    ...getApplicableMigrations(_migrations, project.metadata.appVersion, currentAppVersion),
  ];

  const needsSessionMigration = applicable.some(([, e]) => e.session);
  const needsFields = applicable.some(([, e]) => e.uiInputs || e.customData);
  const needsPending = applicable.some(([, e]) => e.pending || e.session) && !!project.pendingParams;
  const needsStructuralMerge = !!project.paramateSession && !!currentSessionBlob;

  if (!needsSessionMigration && !needsFields && !needsPending && !needsStructuralMerge) return project;

  let { uiInputs, customData, paramateSession } = project;
  // Snapshots before any migration in the chain runs, so later transforms can
  // compare their progressively-migrated value against the as-saved one.
  const initUiInputs = uiInputs;
  const initCustomData = customData ?? {};

  for (const [, entry] of applicable) {
    if (entry.uiInputs)  uiInputs   = applyFieldMigration(uiInputs, entry.uiInputs, initUiInputs);
    if (entry.customData && customData)
      customData = applyFieldMigration(customData, entry.customData, initCustomData);
  }

  // Session and pending keys the internal chain deleted. App pending transforms
  // get this, so they can tell a library removal from a key that was never set.
  const droppedKeys: string[] = [];

  let migratedSession: SessionParams = {};
  if (paramateSession && (needsSessionMigration || needsStructuralMerge)) {
    const sessionMigrations = applicable
      .filter(([, e]) => e.session)
      .map(([, e]) => e.session!);
    const internalSessionCount = internal.filter(([, e]) => e.session).length;
    const processed = await processSession(
      paramateSession, sessionMigrations, currentSessionBlob, internalSessionCount);
    paramateSession = processed.blob;
    migratedSession = processed.flat;
    droppedKeys.push(...processed.internalDropped);
  }

  // Pending params last, because a `pending` transform is handed the session
  // params as the migrations left them.
  let pending: SessionParams = { ...(project.pendingParams ?? {}) };
  const initPending = pending;
  applicable.forEach(([, entry], i) => {
    // Pending keys live in the same "pkg:param" space as session keys, so a
    // rename or delete written as a `session` MAPPING applies here too — nobody
    // should have to write the same rename twice. Session TRANSFORMS can't be
    // carried over: they're written against a full param map, not the sparse
    // pending one, so what they imply for pending has to be said explicitly.
    const before = i < internal.length ? Object.keys(pending) : null;

    if (entry.session && typeof entry.session !== 'function') {
      pending = applyFieldMigration(pending, entry.session, initPending);
    }
    if (entry.pending) {
      pending = applyFieldMigration(pending, entry.pending, initPending, migratedSession, droppedKeys);
    }

    if (before) for (const key of before) if (!(key in pending)) droppedKeys.push(key);
  });

  return {
    ...project,
    uiInputs,
    customData,
    paramateSession,
    // Same convention as storeSession: an empty map is stored as undefined, so
    // a migration that clears every pending key leaves nothing to replay.
    pendingParams: Object.keys(pending).length > 0 ? pending : undefined,
  };
}
